import { useQueries } from '@tanstack/react-query'
import { IChartContent, IWidgetList } from 'pages/authentication/dashboard/dashboard/tabs/produtividade/card-chart'
import React, { Dispatch, PropsWithChildren, createContext, useContext, useState } from 'react'
import { getCatalogs, getDashboardCount, getDashboardData } from 'services/dashboard-request'
import { dayjs } from 'utils/custom-date'
import { ICatalogs } from 'utils/dashboard-oportunidades-util/types'
import { useAuth } from './auth'

interface IFilterFormInput {
  organization: string
  project: string
  startDate: string
  endDate: string
}

interface IDashCountResponse {
  key: string
  value: number
}

interface IDashboardContextData {
  tabsOptions: string[]
  projectExecutionOptions: any[]
  chart: IChartContent[]
  widgets: IWidgetList[]
  catalogsData: ICatalogs[]
  currentTab: number
  isLoading: boolean
  filterParams: IFilterFormInput
  setCurrentTab: Dispatch<number>
  setFilterParams: Dispatch<IFilterFormInput>
  changeOnCurrentOrg: string
  setChangeOnCurrentOrg: Dispatch<React.SetStateAction<string>>
}

interface DashboardProviderProps extends PropsWithChildren {
  type: 'seller' | 'buyer'
}

const DashboardContext = createContext<IDashboardContextData>({} as IDashboardContextData)

const DashboardProvider: React.FC<DashboardProviderProps> = ({ type, children }) => {
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0)
  const { user } = useAuth()

  const actualDate = dayjs()
  const dateOneMonthAgo = actualDate.subtract(1, 'month')

  const [filterParams, setFilterParams] = useState<IFilterFormInput>({
    organization: 'Todos',
    project: 'Todos',
    startDate: dateOneMonthAgo.startOf('week').format('DD/MM/YYYY'),
    endDate: actualDate.format('DD/MM/YYYY'),
  })

  const [changeOnCurrentOrg, setChangeOnCurrentOrg] = useState<string>('')

  const tabsOptionsSeller: string[] = [
    'Produtividade',
    'Oportunidades',
    'Riscos & Compliance',
    'Working Capital',
    'Conectividade & Segurança',
  ]

  const tabOptionsBuyer: string[] = [
    'Ativos e funcionarios',
    'Compras e movimentações',
    'Direitos por contrato',
    'Meus pagamentos',
    'Minha contabilidade de gastos',
    'Meus riscos',
  ]

  const [countResponse, chartDataResponse, catalogsResponse] = useQueries({
    queries: [
      {
        queryKey: [
          'dashboard',
          'count',
          type === 'seller' && filterParams.organization,
          filterParams.startDate,
          filterParams.endDate,
          changeOnCurrentOrg,
          user && user.organization,
        ],
        queryFn: () => getDashboardCount(filterParams),
        enabled: type === 'seller' || type === 'buyer',
      },
      {
        queryKey: [
          'dashboard',
          'chartData',
          type === 'seller' && filterParams.organization,
          filterParams.startDate,
          filterParams.endDate,
          changeOnCurrentOrg,
          user && user.organization,
        ],
        queryFn: () => getDashboardData(filterParams),
        enabled: type === 'seller' || type === 'buyer',
      },
      {
        queryKey: [
          'dashboard',
          'catalogs',
          type === 'seller' && filterParams.organization,
          filterParams.startDate,
          filterParams.endDate,
          changeOnCurrentOrg,
          user && user.organization,
        ],
        queryFn: () => getCatalogs(filterParams),
        enabled: type === 'seller' || type === 'buyer',
      },
    ],
  })

  let isLoading = true

  if (type === 'seller' || type === 'buyer') {
    isLoading = countResponse.isLoading || chartDataResponse.isLoading || catalogsResponse.isLoading
  }

  const chart: IChartContent[] = chartDataResponse.data?.data || []
  const catalogsData: ICatalogs[] = catalogsResponse.data?.data || []

  let widgets: IWidgetList[] = []

  if (countResponse.data) {
    widgets = countResponse.data.map((item: IDashCountResponse) => ({
      title: item.key,
      value: item.value,
    }))
  }

  return (
    <DashboardContext.Provider
      value={{
        tabsOptions: type === 'seller' ? tabsOptionsSeller : tabOptionsBuyer,
        projectExecutionOptions: [],
        chart,
        widgets,
        isLoading,
        catalogsData,
        filterParams,
        changeOnCurrentOrg,
        setChangeOnCurrentOrg,
        setFilterParams,
        currentTab: currentTabIndex,
        setCurrentTab: setCurrentTabIndex,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}

const useDashboard = (): IDashboardContextData => useContext(DashboardContext)

export { DashboardProvider, useDashboard }
